import { Subject } from 'rxjs';
import createDropdownMenu from '@components/dropdownMenu';
import generateGUID from 'utils/guidGenerator';
import MoveInDateRange from 'enums/moveInDateRange';

// `key` is what's emitted from getObservable()/read by setValue() — a MoveInDateRange key, or
// `null` for "no filter" (the default, "Move anytime"; MoveInDateRange has no member for it, since
// the absence of a filter already means exactly that). `listLabel` is shown for the option inside
// the open list (compact, e.g. "<30 days"); `displayLabel` is shown in the field once the option
// is selected and the list is collapsed (a full sentence, e.g. "Move within 30 days").
export const MOVE_IN_DATE_OPTIONS = [
  { key: null, listLabel: 'Move anytime', displayLabel: 'Move anytime' },
  { key: MoveInDateRange.ThirtyDays.toString(), listLabel: '<30 days', displayLabel: 'Move within 30 days' },
  { key: MoveInDateRange.NinetyDays.toString(), listLabel: '<90 days', displayLabel: 'Move within 90 days' },
  { key: MoveInDateRange.SixMonths.toString(), listLabel: '<6 months', displayLabel: 'Move within 6 months' },
];
const DEFAULT_OPTION = MOVE_IN_DATE_OPTIONS[0];
const OPTION_BY_KEY = new Map(MOVE_IN_DATE_OPTIONS.map((option) => [option.key, option]));

/**
 * Move-in date filter — single-select dropdown (Figma "Move-in date" field), built on the
 * generic `@components/dropdownMenu`. "Move anytime" is the pre-applied default.
 *
 * Emits a `MoveInDateRange` key (or `null` for "Move anytime") via `getObservable()`/`setValue()`
 * — the same vocabulary a consuming feature's own filtering already speaks, so it needs no
 * translation layer of its own.
 *
 * Only produces markup (`getHtml`) and binds behavior to the element the calling control created
 * from it (`new MoveInDateFilter(element)`); it never creates DOM itself.
 */
export default class MoveInDateFilter {
  static getSelector() {
    return '[data-move-in-date-filter]';
  }

  static getHtml() {
    const labelId = `move-in-date-label-${generateGUID()}`;
    const buttonId = `move-in-date-button-${generateGUID()}`;
    // A DOM attribute can't hold a real `null` — an empty string stands in for the default
    // option's `key: null` here, and is converted back in the constructor's subscription.
    const optionsHtml = MOVE_IN_DATE_OPTIONS.map(({ key, listLabel }) => `
      <li>
        <button type="button"
                class="nhs-c-dropdown__option nhs-c-dropdown__option--button"
                data-dropdown-option
                data-value="${key ?? ''}">
          ${listLabel}
        </button>
      </li>
    `).join('');

    return `
      <div class="nhs-c-input__wrapper nhs-c-input__wrapper--filter" data-move-in-date-filter>
        <p class="nhs-c-input__label" id="${labelId}">Move-in date</p>
        <button type="button"
                id="${buttonId}"
                aria-labelledby="${labelId} ${buttonId}"
                class="nhs-c-select-button nhs-c-select-button--modal"
                data-dropdown-toggle
                data-qa="move_in_date_filter">
          <span data-value-display>${DEFAULT_OPTION.displayLabel}</span>
        </button>
        <ul class="nhs-c-dropdown__list nhs-c-dropdown__list--anchored" data-dropdown-list hidden>
          ${optionsHtml}
        </ul>
      </div>
    `;
  }

  constructor(element) {
    this.element = element;
    this.key = DEFAULT_OPTION.key;
    this.valueDisplay = element.querySelector('[data-value-display]');
    this.menu = createDropdownMenu(element);
    this.value$ = new Subject();
    this.subscription = this.menu.getObservable().subscribe((domValue) => {
      const key = domValue || null;
      this.setValue(key);
      this.value$.next(key);
    });
  }

  getObservable() {
    return this.value$.asObservable();
  }

  // Syncs the field to an externally-known value (e.g. the store's current filter, read back
  // when the modal reopens) without emitting — this reflects state that already exists, it isn't
  // a new user selection.
  setValue(key) {
    this.key = key;
    this.valueDisplay.textContent = (OPTION_BY_KEY.get(key) ?? DEFAULT_OPTION).displayLabel;
  }

  destroy() {
    this.subscription.unsubscribe();
    this.menu.destroy();
  }
}
